import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppSelect from '@nxsys/components/AppSelect';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import {
  paymentModelOptions,
  paymentModelOptionsIreland,
  timesheetStatusOptions,
} from '@nxsys/constants/AppConstants';
import { IRELAND } from '@nxsys/store/constants/keys';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { GetListTimesheetsParams } from '@nxsys/store/slices/timesheets/type';
import { debounce } from 'lodash';
import { useCallback, useState } from 'react';
import FilterModal from './FilterModal';
import { AppState } from '@nxsys/store';
import { useSelector } from 'react-redux';
import AppSelectSync from '@nxsys/components/AppSelectSync';

type HeaderProps = {
  isAwaitingInvoice?: boolean;
  isPendingApproval?: boolean;
  showImportFilter?: boolean;
  onFilter: (params: GetListTimesheetsParams) => void;
};

type FilterValues = {
  taxYear: number;
  status: string;
  search: string;
  weekEndDateFrom: string;
  weekEndDateTo: string;
  taxPeriodFrom: number;
  taxPeriodTo: number;
  batchFrom: number;
  batchTo: number;
  companyRefs: string[];
  agencyRefs: string[];
  paymentModel: string;
  importId: string;
};

const HeaderListing = ({
  isAwaitingInvoice = false,
  isPendingApproval = false,
  showImportFilter = false,
  onFilter,
}: HeaderProps) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const {
    timesheetsData: { queryParams },
  } = useSelector((state: AppState) => state.timesheetsStore);

  const { isEmployee, isAdmin, isAgency } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [isOpenFilter, setIsOpenFilter] = useState(false);

  const handleFilterChange = (params: Partial<FilterValues>) => {
    const newParams = { ...queryParams, ...params };
    onFilter(newParams as GetListTimesheetsParams);
  };

  const searchDebounce = useCallback(
    debounce((search: string) => handleFilterChange({ search }), 500),
    [],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    searchDebounce(searchValue);
  };

  const toggleFilter = () => {
    setIsOpenFilter(!isOpenFilter);
  };

  const renderQuickFilters = () => {
    if (isEmployee || isAgency) return null;

    if (isAwaitingInvoice || isPendingApproval) {
      return (
        <>
          <AppSelectSync
            mode="multiple"
            placeholder={
              isBureau
                ? messages['common.selectEmployer']
                : messages['form.selectCompany']
            }
            type="company"
            apiType="searchCompany"
            onChangeValue={(value) => {
              handleFilterChange({
                companyRefs: value?.map((item: any) => item.value) ?? undefined,
              });
            }}
            className="w-[200px]"
          />
          {!isBureau && (
            <AppSelectSync
              mode="multiple"
              placeholder={messages['form.selectAgency']}
              type="agency"
              apiType="searchAgency"
              onChangeValue={(value) => {
                handleFilterChange({
                  agencyRefs:
                    value?.map((item: any) => item.value) ?? undefined,
                });
              }}
              className="w-[200px]"
            />
          )}
          {isPendingApproval && (
            <AppSelect
              options={isIreland ? paymentModelOptionsIreland : paymentModelOptions}
              placeholder={messages['form.selectPaymentModel']}
              onChange={(option) =>
                handleFilterChange({
                  paymentModel: option?.value,
                })
              }
              className="!w-[220px]"
            />
          )}
        </>
      );
    }

    return (
      <>
        <TaxYearSelect
          onFilter={(value) =>
            handleFilterChange({ taxYear: value.taxYear, importId: undefined })
          }
        />
        {!isAdmin && (
          <AppSelect
            options={timesheetStatusOptions}
            placeholder={messages['form.selectStatus']}
            onChange={(option) => handleFilterChange({ status: option?.value })}
          />
        )}
      </>
    );
  };

  return (
    <div className="TimesheetHeader w-full">
      <div className="flex justify-between gap-x-4 overflow-x-auto">
        {!isEmployee && (
          <div className="flex gap-x-4">{renderQuickFilters()}</div>
        )}
        <div className="flex gap-x-4">
          <AppInputSearch onChange={handleSearch} />
          <AppMenuFilter
            options={[]}
            onChange={() => {}}
            onClick={toggleFilter}
          />
        </div>
      </div>
      <FilterModal
        isOpen={isOpenFilter}
        toggle={toggleFilter}
        onFilter={onFilter}
        isPendingApproval={isPendingApproval}
        showImportFilter={showImportFilter}
      />
    </div>
  );
};

export default HeaderListing;
